export { chainCommands, chainableEditorState, convertCommand, nonChainable } from '@remirror/pm';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import { bool, isObject, isNullOrUndefined, sort, isNumber, Cast, isString, isFunction, clamp, invariant, keys, omit, unset, isArray, isEmptyObject, object, entries, isEmptyArray, findMatches } from '@remirror/core-helpers';
import { EditorState, Transaction, TextSelection, AllSelection, Selection, NodeSelection, Plugin, PluginKey } from '@remirror/pm/state';
import { ReplaceStep, liftTarget, findWrapping } from '@remirror/pm/transform';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import { LEAF_NODE_REPLACING_CHARACTER, ErrorConstant } from '@remirror/core-constants';
import { NodeType, MarkType, Node as Node$1, Mark, Schema, ResolvedPos, Fragment, DOMSerializer, DOMParser, Slice } from '@remirror/pm/model';
import { InputRule } from '@remirror/pm/inputrules';
import { markActiveInRange } from '@remirror/pm/suggest';

/// <reference types="node" />
/**
 * A object with flags identifying the current environment.
 */

var environment = {
  /**
   * Verifies that the environment has both a window and window.document
   */
  get isBrowser() {
    return bool( "object".document !== 'undefined' && window.navigator && window.navigator.userAgent);
  },

  /**
   * Verifies that the environment is JSDOM
   */
  get isJSDOM() {
    return environment.isBrowser && window.navigator.userAgent.includes('jsdom');
  },

  /**
   * Verifies that the environment has a nodejs process and is therefore a node environment
   */
  get isNode() {
    return typeof process !== 'undefined' && process.versions != null && process.versions.node != null;
  },

  /**
   * True when on an iOS device.
   */
  get isIos() {
    return environment.isBrowser && /iPod|iPhone|iPad/.test(navigator.platform);
  },

  /**
   * True when running on macOS
   */
  get isMac() {
    return environment.isBrowser && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  },

  /**
   * Verify that this is an apple device either on the client or server.
   */
  get isApple() {
    return environment.isNode ? process.platform === 'darwin' : environment.isBrowser ? /Mac|iPod|iPhone|iPad/.test(window.navigator.platform) : false;
  },

  /**
   * True when running in DEVELOPMENT environment
   */
  get isDevelopment() {
    return process.env.NODE_ENV === 'development';
  },

  /**
   * True when running unit tests
   */
  get isTest() {
    return process.env.NODE_ENV === 'test';
  },

  /**
   * True when running in PRODUCTION environment
   */
  get isProduction() {
    return process.env.NODE_ENV === 'production';
  }

};

/**
 * Check to see if the passed value is a NodeType.
 *
 * @param value - the value to check
 */

function isNodeType(value) {
  return isObject(value) && value instanceof NodeType;
}
/**
 * Check to see if the passed value is a MarkType.
 *
 * @param value - the value to check
 */

function isMarkType(value) {
  return isObject(value) && value instanceof MarkType;
}
/**
 * Checks to see if the passed value is a ProsemirrorNode
 *
 * @param value - the value to check
 */

function isProsemirrorNode(value) {
  return isObject(value) && value instanceof Node$1;
}
/**
 * Checks to see if the passed value is a ProsemirrorMark
 *
 * @param value - the value to check
 */

function isProsemirrorMark(value) {
  return isObject(value) && value instanceof Mark;
}
/**
 * Checks to see if the passed value is a Prosemirror Editor State
 *
 * @param value - the value to check
 */

function isEditorState(value) {
  return isObject(value) && value instanceof EditorState;
}
/**
 * Checks to see if the passed value is a Prosemirror Transaction
 *
 * @param value - the value to check
 */

function isTransaction(value) {
  return isObject(value) && value instanceof Transaction;
}
/**
 * Checks to see if the passed value is an instance of the editor schema
 *
 * @param value - the value to check
 */

function isEditorSchema(value) {
  return isObject(value) && value instanceof Schema;
}
/**
 * Predicate checking whether the selection is a `TextSelection`.
 *
 * @param value - the value to check
 */

function isTextSelection(value) {
  return isObject(value) && value instanceof TextSelection;
}
/**
 * Predicate checking whether the selection is an `AllSelection`.
 *
 * @param value - the value to check
 */

function isAllSelection(value) {
  return isObject(value) && value instanceof AllSelection;
}
/**
 * Predicate checking whether the value is a Selection
 *
 * @param value - the value to check
 */

function isSelection(value) {
  return isObject(value) && value instanceof Selection;
}
/**
 * Predicate checking whether the value is a ResolvedPosition.
 *
 * @param value - the value to check
 */

function isResolvedPos(value) {
  return isObject(value) && value instanceof ResolvedPos;
}
/**
 * Predicate checking whether the selection is a NodeSelection
 *
 * @param value - the value to check
 */

function isNodeSelection(value) {
  return isObject(value) && value instanceof NodeSelection;
}

/**
 * Checks that a mark is active within the selected region, or the current
 * selection point is within a region with the mark active. Used by extensions
 * to implement their active methods.
 */
function isMarkActive(parameter) {
  var trState = parameter.trState,
      type = parameter.type,
      from = parameter.from,
      to = parameter.to;
  var selection = trState.selection,
      doc = trState.doc,
      storedMarks = trState.storedMarks;
  var $from = selection.$from,
      empty = selection.empty;

  if (from && to) {
    try {
      return Math.max(from, to) < doc.nodeSize && doc.rangeHasMark(from, to, type);
    } catch (_unused) {
      return false;
    }
  }

  if (empty) {
    return bool(type.isInSet(storedMarks !== null && storedMarks !== void 0 ? storedMarks : $from.marks()));
  }

  return doc.rangeHasMark(selection.from, selection.to, type);
}
/**
 * Check if the specified type (NodeType) can be inserted at the current
 * selection point.
 *
 * @param state - the editor state
 * @param type - the node type
 */

function canInsertNode(state, type) {
  var $from = state.selection.$from;

  for (var depth = $from.depth; depth >= 0; depth--) {
    var index = $from.index(depth);

    try {
      if ($from.node(depth).canReplaceWith(index, index, type)) {
        return true;
      }
    } catch (_unused2) {
      return false;
    }
  }

  return false;
}
/**
 * Checks if a node looks like an empty document.
 *
 * @param node - the prosemirror node
 */

function isDocNodeEmpty(node) {
  var nodeChild = node.content.firstChild;

  if (node.childCount !== 1 || !nodeChild) {
    return false;
  }

  return nodeChild.type.isBlock && !nodeChild.childCount && nodeChild.nodeSize === 2 && (isNullOrUndefined(nodeChild.marks) || nodeChild.marks.length === 0);
}
/**
 * Checks if the current node is a block node and empty.
 *
 * @param node - the prosemirror node
 */

function isEmptyBlockNode(node) {
  return bool(node) && node.type.isBlock && !node.textContent && !node.childCount;
}
/**
 * Retrieve the attributes for a mark.
 *
 * @param trState - the editor state or a transaction
 * @param type - the mark type
 */

function getMarkAttributes(trState, type) {
  // Get the current range of the cursor selection.
  var _trState$selection = trState.selection,
      from = _trState$selection.from,
      to = _trState$selection.to; // The container which will be used to store the marks.

  var marks = []; // Find the nodes and add all the marks contained to the above mark container.

  trState.doc.nodesBetween(from, to, node => {
    marks.push(...node.marks);
  }); // Search for the first mark with the same type as requested

  var mark = marks.find(markItem => markItem.type.name === type.name); // Return the mark attrs when found.

  if (mark) {
    return mark.attrs;
  } // Return false to denote the mark could not be found.


  return false;
}

/**
 * Retrieve the `start` and `end` position of a mark. The `$pos` value should be
 * calculated via `tr.doc.resolve(number)`.
 *
 * @remarks
 *
 * @param $pos - the resolved ProseMirror position
 * @param type - the mark type
 */
function getMarkRange($pos, type) {
  // Nothing can be done if neither the position or the type have been provided.
  if (!$pos || !type) {
    return;
  } // Get the start position of the current node that the `$pos` value was
  // calculated for.


  var start = $pos.parent.childAfter($pos.parentOffset); // If the position provided was incorrect and no node exists for this start
  // position exit early.

  if (!start.node) {
    return;
  } // Find the mark if it exists.


  var mark = start.node.marks.find((_ref) => {
    var markType = _ref.type;
    return markType === type;
  }); // If the mark wasn't found then no range can be calculated. Exit early.

  if (!mark) {
    return;
  }

  var startIndex = $pos.index();
  var startPos = $pos.start() + start.offset;
  var endIndex = startIndex + 1;
  var endPos = startPos + start.node.nodeSize;

  while (startIndex > 0 && mark.isInSet($pos.parent.child(startIndex - 1).marks)) {
    startIndex -= 1;
    startPos -= $pos.parent.child(startIndex).nodeSize;
  }

  while (endIndex < $pos.parent.childCount && mark.isInSet($pos.parent.child(endIndex).marks)) {
    endPos += $pos.parent.child(endIndex).nodeSize;
    endIndex += 1;
  }

  var text = $pos.doc.textBetween(startPos, endPos, LEAF_NODE_REPLACING_CHARACTER, '\n\n');
  return {
    from: startPos,
    to: endPos,
    text,
    mark
  };
}
/**
 * Return true if the step provided an instance of any of the provided step
 * constructors.
 *
 * @param step - the step to check
 * @param StepTypes - the valid Step Constructors. Set to an empty array to
 * accept all Steps.
 */

function isValidStep(step, StepTypes) {
  return StepTypes.length === 0 || StepTypes.some(Constructor => step instanceof Constructor);
}
/**
 * Get all the ranges of changes for the provided transaction.
 *
 * This can be used to gather specific parts of the document which require
 * decorations to be recalculated or where nodes should be updated.
 *
 * This is adapted from the answer
 * [here](https://discuss.prosemirror.net/t/find-new-node-instances-and-track-them/96/7)
 *
 * @param tr - the transaction received with updates applied.
 * @param StepTypes - the valid Step Constructors. Set to an empty array to
 * accept all Steps.
 */


function getChangedRanges(tr) {
  var StepTypes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [ReplaceStep];
  // The holder for the ranges value which will be returned from this function.
  var ranges = [];
  var rawRanges = [];

  var _iterator = _createForOfIteratorHelper(tr.steps),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var step = _step.value;

      if (!isValidStep(step, StepTypes)) {
        continue;
      }

      step.getMap().forEach((_, __, from, to) => {
        rawRanges.push({
          from,
          to
        });
      });
    } // Sort the ranges.

  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  var sortedRanges = sort(rawRanges, (a, z) => a.from - z.from); // Merge sorted ranges into the new range.

  var _iterator2 = _createForOfIteratorHelper(sortedRanges),
      _step2;

  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var _step2$value = _step2.value,
          from = _step2$value.from,
          to = _step2$value.to;
      // The last item in the accumulated `ranges`.
      var lastRange = ranges[ranges.length - 1]; // True when range added has no overlap with the previous end value.

      var noOverlap = !lastRange || lastRange.to < from;

      if (noOverlap) {
        // Add the new range when no overlap is found.
        ranges.push({
          from,
          to
        });
      } else {
        // Update the lastRange's end value.
        lastRange.to = Math.max(lastRange.from, to);
      }
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }

  return ranges;
}
/**
 * Get all the changed node ranges for a provided transaction.
 *
 * @param tr - the transaction received with updates applied.
 * @param StepTypes - the valid Step Constructors. Set to an empty array to
 * accept all Steps.
 */

function getChangedNodeRanges(tr, StepTypes) {
  // The container of the ranges to be returned from this function.
  var nodeRanges = [];
  var ranges = getChangedRanges(tr, StepTypes);

  var _iterator3 = _createForOfIteratorHelper(ranges),
      _step3;

  try {
    for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
      var range = _step3.value;

      try {
        var $from = tr.doc.resolve(range.from);
        var $to = tr.doc.resolve(range.to); // Find the node range for this provided range.

        var nodeRange = $from.blockRange($to); // Make sure a valid node is available.

        if (nodeRange) {
          nodeRanges.push(nodeRange);
        }
      } catch (_unused3) {// Changed ranged outside the document
      }
    }
  } catch (err) {
    _iterator3.e(err);
  } finally {
    _iterator3.f();
  }

  return nodeRanges;
}
/**
 * Retrieves the text content from a slice
 *
 * @remarks
 * A utility that's useful for pulling text content from a slice which is
 * usually created via `selection.content()`
 *
 * @param slice - the prosemirror slice
 */

function getTextContentFromSlice(slice) {
  var _slice$content$firstC, _slice$content$firstC2;

  return (_slice$content$firstC = (_slice$content$firstC2 = slice.content.firstChild) === null || _slice$content$firstC2 === void 0 ? void 0 : _slice$content$firstC2.textContent) !== null && _slice$content$firstC !== void 0 ? _slice$content$firstC : '';
}
/**
 * Takes an empty selection and expands it out to the nearest group not matching
 * the excluded characters.
 *
 * @remarks
 *
 * Can be used to find the nearest selected word. See {@link getSelectedWord}
 *
 * @param state - the editor state or a transaction
 * @param exclude - the regex pattern to exclude
 * @returns false if not a text selection or if no expansion available
 */

function getSelectedGroup(state, exclude) {
  if (!isTextSelection(state.selection)) {
    return false;
  }

  var _state$selection = state.selection,
      from = _state$selection.from,
      to = _state$selection.to;

  var getChar = (start, end) => getTextContentFromSlice(TextSelection.create(state.doc, start, end).content());

  for (var char = getChar(from - 1, from); char && !exclude.test(char); from--, char = getChar(from - 1, from)) {// Step backwards until reaching first excluded character or empty text
    // content.
  }

  for (var _char = getChar(to, to + 1); _char && !exclude.test(_char); to++, _char = getChar(to, to + 1)) {// Step forwards until reaching the first excluded character or empty text
    // content
  }

  if (from === to) {
    return false;
  }

  return {
    from,
    to
  };
}
/**
 * Retrieves the nearest space separated word from the current selection.
 *
 * @remarks
 *
 * This always expands outward so that given: `The tw<start>o words<end>` The
 * selection would become `The <start>two words<end>`
 *
 * In other words it expands until it meets an invalid character.
 *
 * @param state - the editor state or transaction.
 */

function getSelectedWord(state) {
  return getSelectedGroup(state, /[\s\0]/);
}
/**
 * Get matching string from a list or single value
 *
 * @remarks
 * Get attrs can be called with a direct match string or array of string
 * matches. This method should be used to retrieve the required string.
 *
 * The index of the matched array used defaults to 0 but can be updated via the
 * second parameter.
 *
 * @param match - the match(es)
 * @param index - the zero-index point from which to start
 */

function getMatchString(match) {
  var index = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  return Array.isArray(match) ? match[index] : match;
}
/**
 * Checks whether the passed value is a valid dom node
 *
 * @param domNode - the dom node
 */

function isDomNode(domNode) {
  return isObject(Node) ? domNode instanceof Node : isObject(domNode) && isNumber(Cast(domNode).nodeType) && isString(Cast(domNode).nodeName);
}
/**
 * Checks for an element node like `<p>` or `<div>`.
 *
 * @param domNode - the dom node
 */

function isElementDomNode(domNode) {
  return isDomNode(domNode) && domNode.nodeType === Node.ELEMENT_NODE;
}
/**
 * Finds the closest element which matches the passed selector
 *
 * @param domNode - the dom node
 * @param selector - the selector
 */

function closestElement(domNode, selector) {
  if (!isElementDomNode(domNode)) {
    return null;
  }

  if (isNullOrUndefined(document.documentElement) || !document.documentElement.contains(domNode)) {
    return null;
  }

  var matches = isFunction(domNode.matches) ? 'matches' : Cast('msMatchesSelector');

  do {
    var _domNode$parentElemen;

    if (isFunction(domNode[matches]) && domNode[matches](selector)) {
      return domNode;
    }

    domNode = (_domNode$parentElemen = domNode.parentElement) !== null && _domNode$parentElemen !== void 0 ? _domNode$parentElemen : domNode.parentNode;
  } while (isElementDomNode(domNode));

  return null;
}
/**
 * Checks for a text node.
 *
 * @param domNode - the dom node
 */

function isTextDomNode(domNode) {
  return isDomNode(domNode) && domNode.nodeType === Node.TEXT_NODE;
}
/**
 * Retrieve the nearest non-text node.
 *
 * @param domNode - the dom node
 */

function getNearestNonTextElement(domNode) {
  return isTextDomNode(domNode) ? domNode.parentElement : domNode;
}
/**
 * Checks whether the cursor is at the end of the state.doc
 *
 * @param state - the editor state
 */

function atDocEnd(state) {
  return state.doc.nodeSize - state.selection.$to.pos - 2 === state.selection.$to.depth;
}
/**
 * Checks whether the cursor is at the beginning of the state.doc
 *
 * @param state - the editor state
 */

function atDocStart(state) {
  return state.selection.$from.pos === state.selection.$from.depth;
}
/**
 * Get the start position of the parent of the current resolve position
 *
 * @param $pos - the resolved `ProseMirror` position
 */

function startPositionOfParent($pos) {
  return $pos.start($pos.depth);
}
/**
 * Get the end position of the parent of the current resolve position
 *
 * @param $pos - the resolved `ProseMirror` position
 */

function endPositionOfParent($pos) {
  return $pos.end($pos.depth) + 1;
}
/**
 * Retrieve the current position of the cursor
 *
 * @param selection - the editor selection
 * @returns a resolved position only when the selection is a text selection
 */

function getCursor(selection) {
  return isTextSelection(selection) ? selection.$cursor : undefined;
}
/**
 * Checks whether a Prosemirror node is the top level `doc` node
 *
 * @param node - the prosemirror node
 * @param schema - the prosemirror schema
 */

function isDocNode(node, schema) {
  if (!isProsemirrorNode(node)) {
    return false;
  }

  if (schema) {
    return node.type === schema.nodes.doc;
  }

  return node.type.name === 'doc';
}
/**
 * Checks whether the passed in JSON is a valid object node
 *
 * @param value - the value to check
 */

function isRemirrorJSON(value) {
  return isObject(value) && value.type === 'doc' && Array.isArray(value.content);
}

/**
 * Return true when the provided value is an anchor / head selection property
 */
function isAnchorHeadObject(value) {
  return isObject(value) && isNumber(value.anchor) && isNumber(value.head);
}
/**
 * Get a valid selection from the primitive selection.
 */

function getTextSelection(selection, doc) {
  var max = doc.nodeSize - 2;
  var min = 0;
  var pos;
  /** Ensure the selection is within the current document range */

  var clampToDocument = value => clamp({
    min,
    max,
    value
  });

  if (isSelection(selection)) {
    return selection;
  }

  if (selection === 'all') {
    return new AllSelection(doc);
  }

  if (selection === 'start') {
    pos = min;
  } else if (selection === 'end') {
    pos = max;
  } else {
    pos = selection;
  }

  if (isNumber(pos)) {
    pos = clampToDocument(pos);
    return TextSelection.near(doc.resolve(pos));
  }

  if (isAnchorHeadObject(pos)) {
    var _anchor = clampToDocument(pos.anchor);

    var _head = clampToDocument(pos.head);

    return TextSelection.create(doc, _anchor, _head);
  } // In this case assume that `from` is the fixed anchor and `to` is the movable
  // head.


  var anchor = clampToDocument(pos.from);
  var head = clampToDocument(pos.to);
  return TextSelection.create(doc, anchor, head);
}
/**
 * A function that converts a string into a `ProsemirrorNode`.
 */

// The maximum attempts to check invalid content before throwing an an error.
var MAX_ATTEMPTS = 3;
/**
 * Creates a document node from the passed in content and schema.
 *
 * @remirror
 *
 * This supports a primitive form of error handling. When an error occurs, the
 * `onError` handler will be called along with the error produced by the Schema
 * and it is up to you as a developer to decide how to transform the invalid
 * content into valid content.
 *
 * Please note that the `onError` is only called when the content is a JSON
 * object. It is not called for a `string`, the `ProsemirrorNode` or the
 * `EditorState`. The reason for this is that string require a `stringHandler`
 * which is defined by the developer and transforms the content. That is the
 * point that error's should be handled. The editor state and the
 * `ProsemirrorNode` are similar. They need to be created by the developer and
 * as a result, the errors should be handled at the point of creation rather
 * than when the document is being applied to the editor.
 *
 * @param parameter - the destructured create document node params
 */

function createDocumentNode(parameter) {
  var content = parameter.content,
      schema = parameter.schema,
      document = parameter.document,
      stringHandler = parameter.stringHandler,
      onError = parameter.onError,
      _parameter$attempts = parameter.attempts,
      attempts = _parameter$attempts === void 0 ? 0 : _parameter$attempts; // If there is an `onError` handler then check the attempts does not exceed
  // the maximum, otherwise only allow one attempt.

  var attemptsRemaining = onError && attempts <= MAX_ATTEMPTS || attempts === 0;
  !attemptsRemaining ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.INVALID_CONTENT,
    message: 'The invalid content has been called recursively more than ${MAX_ATTEMPTS} times. The content is invalid and the error handler has not been able to recover properly.'
  }) : invariant(false) : void 0;

  if (isString(content)) {
    !stringHandler ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.INVALID_CONTENT,
      message: "The string '".concat(content, "' was added to the editor, but no `stringHandler` was added. Please provide a valid string handler which transforms your content to a `ProsemirrorNode` to prevent this error.")
    }) : invariant(false) : void 0; // With string content it is up to you the developer to ensure there are no
    // errors in the produced content.

    return stringHandler({
      document,
      content,
      schema
    });
  } // If passing in an editor state, it is left to the developer to make sure the
  // state they created is valid.


  if (isEditorState(content)) {
    return content.doc;
  } // When passing the prosemirror no error checking is done. Before creating the
  // node you should manually ensure that it is valid.


  if (isProsemirrorNode(content)) {
    return content;
  } // At this point the only possible solution is that the content is a json
  // object so we try to convert the json to a valid object.


  try {
    // This will throw an error for invalid content.
    return schema.nodeFromJSON(content);
  } catch (error) {
    var details = getInvalidContent({
      schema,
      error,
      json: content
    });
    var transformedContent = onError === null || onError === void 0 ? void 0 : onError(details);
    !transformedContent ? process.env.NODE_ENV !== "production" ? invariant(false, {
      code: ErrorConstant.INVALID_CONTENT,
      message: "An error occurred when processing the content. Please provide an `onError` handler to process the invalid content: ".concat(JSON.stringify(details.invalidContent, null, 2))
    }) : invariant(false) : void 0;
    return createDocumentNode(_objectSpread(_objectSpread({}, parameter), {}, {
      content: transformedContent,
      attempts: attempts + 1
    }));
  }
}
/**
 * Checks which environment should be used. Returns true when we are in the dom
 * environment.
 *
 * @param forceEnvironment - force a specific environment to override the
 * outcome
 */

function shouldUseDomEnvironment(forceEnvironment) {
  return forceEnvironment === 'dom' || environment.isBrowser && !forceEnvironment;
}
/**
 * Retrieves the document based on the environment we are currently in.
 *
 * @param forceEnvironment - force a specific environment
 */

function getDocument(forceEnvironment) {
  {
    return document;
  }
}

/**
 * Convert a node into its DOM representative
 *
 * @param params - the from node params
 */
function toDom(_ref2) {
  var node = _ref2.node,
      schema = _ref2.schema,
      document = _ref2.document;
  var fragment = isDocNode(node, schema) ? node.content : Fragment.from(node);
  return DOMSerializer.fromSchema(schema).serializeFragment(fragment, {
    document
  });
}

/**
 * Convert the provided `node` to a html string.
 *
 * @param params - the from node params
 *
 * ```ts
 * import { EditorState, toHtml } from 'remirror/core';
 *
 * function convertStateToHtml(state: EditorState): string {
 *   return toHtml({ node: state.doc, schema: state.schema });
 * }
 * ```
 */
function toHtml(_ref3) {
  var node = _ref3.node,
      schema = _ref3.schema,
      _ref3$document = _ref3.document,
      document = _ref3$document === void 0 ? getDocument() : _ref3$document;
  var element = document.createElement('div');
  element.append(toDom({
    node,
    schema,
    document
  }));
  return element.innerHTML;
}

/**
 * Convert a HTML string into a ProseMirror node. This can be used for the
 * `stringHandler` property in your editor when you want to support html.
 *
 * ```tsx
 * import { fromHtml } from 'remirror/core';
 * import { RemirrorProvider, useManager } from 'remirror/react';
 *
 * const Editor = () => {
 *   const manager = useManager([]);
 *
 *   return (
 *     <RemirrorProvider
 *       stringHandler={fromHtml}
 *       initialContent='<p>A wise person once told me to relax</p>'
 *     >
 *       <div />
 *     </RemirrorProvider>
 *   );
 * }
 * ```
 */
function fromHtml(parameter) {
  var content = parameter.content,
      schema = parameter.schema,
      _parameter$document = parameter.document,
      document = _parameter$document === void 0 ? getDocument() : _parameter$document;
  var element = document.createElement('div');
  element.innerHTML = content.trim();
  return DOMParser.fromSchema(schema).parse(element);
}
/**
 * A wrapper around `state.doc.toJSON` which returns the state as a
 * `RemirrorJSON` object.
 */

function getRemirrorJSON(content) {
  return isProsemirrorNode(content) ? content.toJSON() : content.doc.toJSON();
}

/**
 * Check if two states are equal.
 */
function areStatesEqual(stateA, stateB) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  // The states are identical, so they're equal.
  if (stateA === stateB) {
    return true;
  } // If the content is different then, no, not equal.


  if (!stateA.doc.eq(stateB.doc)) {
    return false;
  } // If we care about selection and selection is not the same, then not equal.


  if (options.checkSelection && !stateA.selection.eq(stateB.selection)) {
    return false;
  } // If the schema are not compatible then no, not equal.


  if (!areSchemasCompatible(stateA.schema, stateB.schema)) {
    return false;
  }

  return true;
}
/**
 * Check that the nodes and marks present on `schemaA` are also present on
 * `schemaB`.
 */

function areSchemasCompatible(schemaA, schemaB) {
  if (schemaA === schemaB) {
    return true;
  }

  var marksA = keys(schemaA.marks);
  var marksB = keys(schemaB.marks);
  var nodesA = keys(schemaA.nodes);
  var nodesB = keys(schemaB.nodes);

  if (marksA.length !== marksB.length || nodesA.length !== nodesB.length) {
    return false;
  }

  var _iterator4 = _createForOfIteratorHelper(marksA),
      _step4;

  try {
    for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
      var mark = _step4.value;

      // No reverse check needed since we know the keys are unique and the lengths
      // are identical.
      if (!marksB.includes(mark)) {
        return false;
      }
    }
  } catch (err) {
    _iterator4.e(err);
  } finally {
    _iterator4.f();
  }

  var _iterator5 = _createForOfIteratorHelper(nodesA),
      _step5;

  try {
    for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
      var node = _step5.value;

      // No reverse check needed since we know the keys are unique and the lengths
      // are identical.
      if (!nodesB.includes(node)) {
        return false;
      }
    }
  } catch (err) {
    _iterator5.e(err);
  } finally {
    _iterator5.f();
  }

  return true;
}
/**
 * Returns attributes for a node excluding those that were provided as extra attributes
 *
 * @param attrs - The source attributes
 * @param extra - The extra attribute schema for this node
 */

function omitExtraAttributes(attrs, extra) {
  var extraAttributeNames = keys(extra.defaults());
  return omit(_objectSpread({}, attrs), extraAttributeNames);
}
/**
 * A description of an invalid content block (representing a node or a mark).
 */

var transformers = {
  /**
   * Remove every invalid block from the editor. This is a destructive action
   * and should only be applied if you're sure it's the best strategy.
   *
   * @param json - the content as a json object.
   * @param invalidContent - the list of invalid items as passed to the error
   * handler.
   */
  remove(json, invalidContent) {
    var newJSON = json;

    var _iterator6 = _createForOfIteratorHelper(invalidContent),
        _step6;

    try {
      for (_iterator6.s(); !(_step6 = _iterator6.n()).done;) {
        var block = _step6.value;

        if (block.invalidParentNode) {
          continue;
        }

        newJSON = unset(block.path, newJSON);
      }
    } catch (err) {
      _iterator6.e(err);
    } finally {
      _iterator6.f();
    }

    return newJSON;
  }

};

/**
 * Get the invalid parameter which is passed to the `onError` handler.
 */
function getInvalidContent(_ref4) {
  var json = _ref4.json,
      schema = _ref4.schema,
      extra = _objectWithoutProperties(_ref4, ["json", "schema"]);

  var validMarks = new Set(keys(schema.marks));
  var validNodes = new Set(keys(schema.nodes));
  var invalidContent = checkForInvalidContent({
    json,
    path: [],
    validNodes,
    validMarks
  });
  return _objectSpread({
    json,
    invalidContent,
    transformers
  }, extra);
}

/**
 * Get the invalid content from the `RemirrorJSON`.
 */
function checkForInvalidContent(parameter) {
  var json = parameter.json,
      validMarks = parameter.validMarks,
      validNodes = parameter.validNodes,
      _parameter$path = parameter.path,
      path = _parameter$path === void 0 ? [] : _parameter$path;
  var valid = {
    validMarks,
    validNodes
  };
  var invalidNodes = [];
  var type = json.type,
      marks = json.marks,
      content = json.content;
  var _parameter$invalidPar = parameter.invalidParentMark,
      invalidParentMark = _parameter$invalidPar === void 0 ? false : _parameter$invalidPar,
      _parameter$invalidPar2 = parameter.invalidParentNode,
      invalidParentNode = _parameter$invalidPar2 === void 0 ? false : _parameter$invalidPar2;

  if (marks) {
    var invalidMarks = [];

    var _iterator7 = _createForOfIteratorHelper(marks.entries()),
        _step7;

    try {
      for (_iterator7.s(); !(_step7 = _iterator7.n()).done;) {
        var _step7$value = _slicedToArray(_step7.value, 2),
            index = _step7$value[0],
            mark = _step7$value[1];

        var name = isString(mark) ? mark : mark.type;

        if (validMarks.has(name)) {
          continue;
        }

        invalidMarks.unshift({
          name,
          path: [...path, 'marks', "".concat(index)],
          type: 'mark',
          invalidParentMark,
          invalidParentNode
        });
        invalidParentMark = true;
      }
    } catch (err) {
      _iterator7.e(err);
    } finally {
      _iterator7.f();
    }

    invalidNodes.push(...invalidMarks);
  }

  if (!validNodes.has(type)) {
    invalidNodes.push({
      name: type,
      type: 'node',
      path,
      invalidParentMark,
      invalidParentNode
    });
    invalidParentNode = true;
  }

  if (content) {
    var invalidContent = [];

    var _iterator8 = _createForOfIteratorHelper(content.entries()),
        _step8;

    try {
      for (_iterator8.s(); !(_step8 = _iterator8.n()).done;) {
        var _step8$value = _slicedToArray(_step8.value, 2),
            _index = _step8$value[0],
            value = _step8$value[1];

        invalidContent.unshift(...checkForInvalidContent(_objectSpread(_objectSpread({}, valid), {}, {
          json: value,
          path: [...path, 'content', "".concat(_index)],
          invalidParentMark,
          invalidParentNode
        })));
      }
    } catch (err) {
      _iterator8.e(err);
    } finally {
      _iterator8.f();
    }

    invalidNodes.unshift(...invalidContent);
  }

  return invalidNodes;
}

/**
 * Checks if the type a given `node` has a given `nodeType`.
 */
function isNodeOfType(parameter) {
  var types = parameter.types,
      node = parameter.node;

  if (!node) {
    return false;
  }

  var matches = type => {
    return type === node.type || type === node.type.name;
  };

  if (isArray(types)) {
    return types.some(matches);
  }

  return matches(types);
}

/**
 * Creates a new transaction object from a given transaction. This is useful
 * when applying changes to a transaction, that you may want to rollback.
 *
 * ```ts
 * function() applyUpdateIfValid(state: EditorState) {
 *   const tr = cloneTransaction(state.tr);
 *
 *   tr.insertText('hello');
 *
 *   if (!checkValid(tr)) {
 *     return;
 *   }
 *
 *   applyClonedTransaction({ clone: tr, tr: state.tr });
 * }
 * ```
 *
 * The above example applies a transaction to the cloned transaction then checks
 * to see if the changes are still valid and if they are applies the mutative
 * changes to the original state transaction.
 *
 * @param tr - the prosemirror transaction
 */
function cloneTransaction(tr) {
  return Object.assign(Object.create(tr), tr).setTime(Date.now());
}

/**
 * Get the diff between two ordered arrays with a reference equality check.
 */
function diff(primary, other) {
  return primary.filter((item, index) => item !== other[index]);
}
/**
 * Apply the steps of a cloned transaction to the original transaction `tr`.
 */


function applyClonedTransaction(parameter) {
  var clone = parameter.clone,
      tr = parameter.tr;
  var steps = diff(clone.steps, tr.steps);

  var _iterator = _createForOfIteratorHelper(steps),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var step = _step.value;
      tr.step(step);
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }
}
/**
 * Checks if the type a given `node` has a given `nodeType`.
 */

function markEqualsType(parameter) {
  var types = parameter.types,
      mark = parameter.mark;
  return mark ? Array.isArray(types) && types.includes(mark.type) || mark.type === types : false;
}

/**
 * Performs a `delete` transaction that removes a node at a given position with
 * the given `node`. `position` should point at the position immediately before
 * the node.
 *
 * @param position - the prosemirror position
 */
function removeNodeAtPosition(_ref) {
  var pos = _ref.pos,
      tr = _ref.tr;
  var node = tr.doc.nodeAt(pos);

  if (node) {
    tr.delete(pos, pos + node.nodeSize);
  }

  return tr;
}

/**
 * Replaces the node at the provided position with the provided content.
 */
function replaceNodeAtPosition(_ref2) {
  var pos = _ref2.pos,
      tr = _ref2.tr,
      content = _ref2.content;
  var node = tr.doc.nodeAt(pos);

  if (node) {
    tr.replaceWith(pos, pos + node.nodeSize, content);
  }

  return tr;
}
/**
 * Returns DOM reference of a node at a given `position`.
 *
 * @remarks
 *
 * If the node type is of type `TEXT_NODE` it will return the reference of the
 * parent node.
 *
 * A simple use case
 *
 * ```ts
 * const element = findElementAtPosition($from.pos, view);
 * ```
 *
 * @param position - the prosemirror position
 * @param view - the editor view
 */

function findElementAtPosition(position, view) {
  var dom = view.domAtPos(position);
  var node = dom.node.childNodes[dom.offset];

  if (isTextDomNode(dom.node)) {
    return dom.node.parentNode;
  }

  if (isNullOrUndefined(node) || isTextDomNode(node)) {
    return dom.node;
  }

  return node;
}
/**
 * Iterates over parent nodes, returning the closest node and its start position
 * that the `predicate` returns truthy for. `start` points to the start position
 * of the node, `pos` points directly before the node.
 *
 * ```ts
 * const predicate = node => node.type === schema.nodes.blockquote;
 * const parent = findParentNode({ predicate, selection });
 * ```
 */

function findParentNode(parameter) {
  var predicate = parameter.predicate,
      selection = parameter.selection;
  var $pos = isEditorState(selection) ? selection.selection.$from : isSelection(selection) ? selection.$from : selection;

  for (var depth = $pos.depth; depth > 0; depth--) {
    var node = $pos.node(depth);

    if (predicate(node)) {
      var pos = depth > 0 ? $pos.before(depth) : 0;
      var start = $pos.start(depth);
      var end = pos + node.nodeSize;
      return {
        pos,
        depth,
        node,
        start,
        end
      };
    }
  }

  return;
}
/**
 * Finds the node at the resolved position.
 *
 * @param $pos - the resolve position in the document
 */

function findNodeAtPosition($pos) {
  var depth = $pos.depth;
  var pos = depth > 0 ? $pos.before(depth) : 0;
  var node = $pos.node(depth);
  var start = $pos.start(depth);
  var end = pos + node.nodeSize;
  return {
    pos,
    start,
    node,
    end,
    depth
  };
}
/**
 * Finds the node at the passed selection.
 */

function findNodeAtSelection(selection) {
  var parentNode = findParentNode({
    predicate: () => true,
    selection
  });
  !parentNode ? process.env.NODE_ENV !== "production" ? invariant(false, {
    message: 'No parent node found for the selection provided.'
  }) : invariant(false) : void 0;
  return parentNode;
}

/**
 *  Iterates over parent nodes, returning closest node of a given `nodeType`.
 *  `start` points to the start position of the node, `pos` points directly
 *  before the node.
 *
 *  ```ts
 *  const parent = findParentNodeOfType({types: schema.nodes.paragraph, selection});
 *  ```
 */
function findParentNodeOfType(parameter) {
  var types = parameter.types,
      selection = parameter.selection;
  return findParentNode({
    predicate: node => isNodeOfType({
      types,
      node
    }),
    selection
  });
}
/**
 * Returns position of the previous node.
 *
 * ```ts
 * const pos = findPositionOfNodeBefore(tr.selection);
 * ```
 *
 * @param selection - the prosemirror selection
 */

function findPositionOfNodeBefore(value) {
  var $pos = isResolvedPos(value) ? value : isSelection(value) ? value.$from : value.selection.$from;

  if (isNullOrUndefined($pos)) {
    throw new Error('Invalid value passed in.');
  }

  var nodeBefore = $pos.nodeBefore;
  var selection = Selection.findFrom($pos, -1);

  if (!selection || !nodeBefore) {
    return;
  }

  var parent = findParentNodeOfType({
    types: nodeBefore.type,
    selection
  });
  return parent ? parent : {
    node: nodeBefore,
    pos: selection.$from.pos,
    end: selection.$from.end(),
    depth: selection.$from.depth + 1,
    start: selection.$from.start(selection.$from.depth + 1)
  };
}
/**
 * Updates the provided transaction to remove the node before.
 *
 * ```ts
 * dispatch(
 *    removeNodeBefore(state.tr)
 * );
 * ```
 *
 * @param tr
 */

function removeNodeBefore(tr) {
  var result = findPositionOfNodeBefore(tr.selection);

  if (result) {
    removeNodeAtPosition({
      pos: result.pos,
      tr
    });
  }

  return tr;
}

/**
 * Returns a node of a given `nodeType` if it is selected. `start` points to the
 * start position of the node, `pos` points directly before the node.
 *
 * ```ts
 * const { extension, inlineExtension, bodiedExtension } = schema.nodes;
 *
 * const selectedNode = findSelectedNodeOfType({
 *   types: [extension, inlineExtension, bodiedExtension],
 *   selection,
 * });
 * ```
 */
function findSelectedNodeOfType(parameter) {
  var types = parameter.types,
      selection = parameter.selection;

  if (!isNodeSelection(selection) || !isNodeOfType({
    types,
    node: selection.node
  })) {
    return;
  }

  return {
    pos: selection.$from.pos,
    depth: selection.$from.depth,
    start: selection.$from.start(),
    end: selection.$from.pos + selection.node.nodeSize,
    node: selection.node
  };
}

/**
 * Returns the position of the node after the current position, selection or
 * state.
 *
 * ```ts
 * const pos = findPositionOfNodeBefore(tr.selection);
 * ```
 *
 * @param selection - the prosemirror selection
 */
function findPositionOfNodeAfter(value) {
  var $pos = isResolvedPos(value) ? value : isSelection(value) ? value.$from : value.selection.$from;

  if (isNullOrUndefined($pos)) {
    throw new Error('Invalid value passed in.');
  }

  var nodeAfter = $pos.nodeAfter;
  var selection = Selection.findFrom($pos, 1);

  if (!selection || !nodeAfter) {
    return;
  }

  var parent = findParentNodeOfType({
    types: nodeAfter.type,
    selection
  });
  return parent ? parent : {
    node: nodeAfter,
    pos: selection.$from.pos,
    end: selection.$from.end(),
    depth: selection.$from.depth + 1,
    start: selection.$from.start(selection.$from.depth + 1)
  };
}
/**
 * Update the transaction to delete the node after the current selection.
 *
 * ```ts
 * dispatch(removeNodeBefore(state.tr));
 * ```
 *
 * @param tr
 */

function removeNodeAfter(tr) {
  var result = findPositionOfNodeAfter(tr.selection);

  if (result) {
    removeNodeAtPosition({
      pos: result.pos,
      tr
    });
  }

  return tr;
}
/**
 * Checks whether the selection or state is currently empty.
 *
 * @param value - the transaction selection or state
 */

function isSelectionEmpty(value) {
  if (isSelection(value)) {
    return value.empty;
  }

  if (isTransaction(value) || isEditorState(value)) {
    return value.selection.empty;
  }

  return true;
}
/**
 * Check to see if a transaction has changed either the document or the current
 * selection.
 *
 * @param tr - the transaction to check
 */

function hasTransactionChanged(tr) {
  return tr.docChanged || tr.selectionSet;
}

/**
 * Checks whether the node type passed in is active within the region. Used by
 * extensions to implement the `active` method.
 *
 * To ignore `attrs` just leave the attrs object empty or undefined.
 *
 * @param params - the destructured node active parameters
 */
function isNodeActive(parameter) {
  var _findSelectedNodeOfTy;

  var state = parameter.state,
      type = parameter.type,
      _parameter$attrs = parameter.attrs,
      attrs = _parameter$attrs === void 0 ? {} : _parameter$attrs;
  var selection = state.selection;

  var predicate = node => node.type === type;

  var parent = (_findSelectedNodeOfTy = findSelectedNodeOfType({
    selection,
    types: type
  })) !== null && _findSelectedNodeOfTy !== void 0 ? _findSelectedNodeOfTy : findParentNode({
    predicate,
    selection
  });

  if (!attrs || isEmptyObject(attrs) || !parent) {
    return bool(parent);
  }

  return parent.node.hasMarkup(type, _objectSpread(_objectSpread({}, parent.node.attrs), attrs));
}
/**
 * The ProseMirror `Schema` as a JSON object.
 */

/**
 * Converts a `schema` to a JSON compatible object.
 */
function schemaToJSON(schema) {
  var nodes = object();
  var marks = object();

  var _iterator2 = _createForOfIteratorHelper(entries(schema.nodes)),
      _step2;

  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var _step2$value = _slicedToArray(_step2.value, 2),
          key = _step2$value[0],
          spec = _step2$value[1].spec;

      nodes[key] = spec;
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }

  var _iterator3 = _createForOfIteratorHelper(entries(schema.marks)),
      _step3;

  try {
    for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
      var _step3$value = _slicedToArray(_step3.value, 2),
          _key = _step3$value[0],
          _spec = _step3$value[1].spec;

      marks[_key] = _spec;
    }
  } catch (err) {
    _iterator3.e(err);
  } finally {
    _iterator3.f();
  }

  return {
    nodes,
    marks
  };
}
/**
 * Chains together keybindings, allowing for the same key binding to be used
 * across multiple extensions without overriding behavior.
 *
 * @remarks
 *
 * When `next` is called it hands over full control of the keybindings to the
 * function that invokes it.
 */

function chainKeyBindingCommands() {
  for (var _len = arguments.length, commands = new Array(_len), _key2 = 0; _key2 < _len; _key2++) {
    commands[_key2] = arguments[_key2];
  }

  return parameters => {
    // When no commands are passed just ignore and continue.
    if (isEmptyArray(commands)) {
      return false;
    }

    var command = commands[0],
        rest = commands.slice(1); // Keeps track of whether the `next` method has been called. If it has been
    // called we return the result and skip the rest of the downstream commands.

    var calledNext = false;
    /**
     * Create the next function call. Updates the outer closure when the next
     * method has been called.
     */

    var createNext = function createNext() {
      for (var _len2 = arguments.length, nextCommands = new Array(_len2), _key3 = 0; _key3 < _len2; _key3++) {
        nextCommands[_key3] = arguments[_key3];
      }

      return () => {
        // If there are no commands then this can be ignored and continued.
        if (isEmptyArray(nextCommands)) {
          return false;
        } // Update the closure with information that the next method was invoked by
        // this command.


        calledNext = true;
        var nextRest = nextCommands.slice(1); // Recursively call the key bindings method.

        return chainKeyBindingCommands(...nextCommands)(_objectSpread(_objectSpread({}, parameters), {}, {
          next: createNext(...nextRest)
        }));
      };
    };

    var next = createNext(...rest);
    var exitEarly = command(_objectSpread(_objectSpread({}, parameters), {}, {
      next
    })); // Exit the chain of commands early if either:
    // - a) next was called
    // - b) the command returned true

    if (calledNext || exitEarly) {
      return exitEarly;
    } // Continue to the next function in the chain of commands.


    return next();
  };
}
/**
 * Used to merge key bindings together in a sensible way. Identical key bindings
 * likely have the same key. as a result a naive merge would result in the
 * binding added later in the merge being the only one the editor sees.
 *
 * This creator is used to create a merge that steps from the highest priority
 * to the lowest priority giving each keybinding in the chain an opportunity to
 * be run, and defer to the next command in the chain or choose not to.
 *
 * - It is used to create the [[`mergeKeyBindings`]] function helper.
 * - It is used to create the [[`mergeProsemirrorKeyBindings`]] function helper.
 *
 * @typeParam [Schema] - the schema that is being used to create this command.
 * @typeParam [Type] - the mapper type signature which is what the `mapper`
 * param transforms the [[`KeyBindingCommandFunction`]]  into.
 *
 * @param extensionKeymaps - the list of extension keymaps similar to the
 * following:
 *   ```ts
 *     [{ Enter: () => false}, { Escape: () => true }, { Enter: () => true }]
 *   ```
 * @param mapper - used to convert the [[`KeyBindingCommandFunction`]] into a
 * function with a different signature. It's application can be seen in
 * [[`mergeKeyBindings`]] and [[`mergeProsemirrorKeyBindings`]].
 *
 */

function mergeKeyBindingCreator(extensionKeymaps, mapper) {
  // Keep track of the previous commands as we loop through the `extensionKeymaps`.
  var previousCommandsMap = new Map(); // This is the combined mapping of commands. Essentially this function turns
  // the `extensionKeymaps` array into a single object `extensionKeymap` which
  // composes each function to give full control to the developer.

  var mappedCommands = object(); // Outer loop iterates over each object keybinding.

  var _iterator4 = _createForOfIteratorHelper(extensionKeymaps),
      _step4;

  try {
    for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
      var extensionKeymap = _step4.value;

      // Inner loop checks each keybinding on the keybinding object. `key` refers
      // to the name of the keyboard combination, like `Shift-Enter` or
      // `Cmd-Escape`.
      var _iterator5 = _createForOfIteratorHelper(entries(extensionKeymap)),
          _step5;

      try {
        for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
          var _previousCommandsMap$;

          var _step5$value = _slicedToArray(_step5.value, 2),
              key = _step5$value[0],
              newCommand = _step5$value[1];

          // Get the previous commands for this key if it already exists
          var previousCommands = (_previousCommandsMap$ = previousCommandsMap.get(key)) !== null && _previousCommandsMap$ !== void 0 ? _previousCommandsMap$ : []; // Update the commands array. This will be added to the
          // `previousCommandsMap` to track the current keyboard combination.

          var commands = [...previousCommands, newCommand]; // Chain the keyboard binding so that you have all the niceties, like
          // being able to call `next` to run the remaining commands in the chain.

          var _command = chainKeyBindingCommands(...commands); // Update the previous commands with the new commands that are now being used.


          previousCommandsMap.set(key, commands); // Store a copy of the mapped commands. If this was the last time this
          // loop ran, then this is the command that would be called when a users
          // enters the keyboard combination specified by the `key` in this context.

          mappedCommands[key] = mapper(_command);
        }
      } catch (err) {
        _iterator5.e(err);
      } finally {
        _iterator5.f();
      }
    }
  } catch (err) {
    _iterator4.e(err);
  } finally {
    _iterator4.f();
  }

  return mappedCommands;
}
/**
 * This merges an array of keybindings into one keybinding with the priority
 * given to the items earlier in the array. `index: 0` has priority over `index:
 * 1` which has priority over `index: 2` and so on.
 *
 * This is for use on remirror keybindings. See `mergeProsemirrorKeyBindings`
 * for transforming the methods into `ProsemirrorCommandFunction`'s.
 */


function mergeKeyBindings(extensionKeymaps) {
  return mergeKeyBindingCreator(extensionKeymaps, command => command);
}
/**
 * This merges an array of keybindings into one keybinding with the priority
 * given to the items earlier in the array. `index: 0` has priority over `index:
 * 1` which has priority over `index: 2` and so on.
 *
 * This supports the [[ProsemirrorCommandFunction]] type signature where the
 * `state`, `dispatch` and `view` are passed as separate arguments.
 */

function mergeProsemirrorKeyBindings(extensionKeymaps) {
  return mergeKeyBindingCreator(extensionKeymaps, // Convert the command to have a signature of the
  // [[`ProsemirrorCommandFunction`]].
  command => (state, dispatch, view) => {
    return command({
      state,
      dispatch,
      view,
      tr: state.tr,
      next: () => false
    });
  });
}

/**
 * Update the selection with the provided MarkType.
 */
function updateMark(parameter) {
  return (_ref) => {
    var dispatch = _ref.dispatch,
        tr = _ref.tr;
    var type = parameter.type,
        _parameter$attrs = parameter.attrs,
        attrs = _parameter$attrs === void 0 ? object() : _parameter$attrs,
        appendText = parameter.appendText,
        range = parameter.range;
    var selection = range ? TextSelection.create(tr.doc, range.from, range.to) : tr.selection;
    var $from = selection.$from,
        from = selection.from,
        to = selection.to;
    var applicable = $from.depth === 0 ? tr.doc.type.allowsMarkType(type) : false;
    tr.doc.nodesBetween(from, to, node => {
      if (applicable) {
        return false;
      }

      if (node.inlineContent && node.type.allowsMarkType(type)) {
        applicable = true;
        return;
      }

      return;
    });

    if (!applicable) {
      return false;
    }

    dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.addMark(from, to, type.create(attrs)) && appendText ? tr.insertText(appendText) : tr);
    return true;
  };
}
/**
 * Lift the selected block, or the closest ancestor block of the selection that
 * can be lifted, out of its parent node.
 *
 * Adapted from
 * https://github.com/ProseMirror/prosemirror-commands/blob/3126d5c625953ba590c5d3a0db7f1009f46f1571/src/commands.js#L212-L221
 */

function lift(_ref2) {
  var tr = _ref2.tr,
      dispatch = _ref2.dispatch;
  var _tr$selection = tr.selection,
      $from = _tr$selection.$from,
      $to = _tr$selection.$to;
  var range = $from.blockRange($to);
  var target = range && liftTarget(range);

  if (!isNumber(target) || !range) {
    return false;
  }

  dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.lift(range, target).scrollIntoView());
  return true;
}
/**
 * Wrap the selection or the provided text in a node of the given type with the
 * given attributes.
 */

function wrapIn(type) {
  var attrs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var range = arguments.length > 2 ? arguments[2] : undefined;
  return function (parameter) {
    var tr = parameter.tr,
        dispatch = parameter.dispatch,
        state = parameter.state;
    var nodeType = isString(type) ? state.schema.nodes[type] : type;

    var _ref3 = range !== null && range !== void 0 ? range : tr.selection,
        from = _ref3.from,
        to = _ref3.to;

    var $from = tr.doc.resolve(from);
    var $to = tr.doc.resolve(to);
    var blockRange = $from.blockRange($to);
    var wrapping = blockRange && findWrapping(blockRange, nodeType, attrs);

    if (!wrapping || !blockRange) {
      return false;
    }

    dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.wrap(blockRange, wrapping).scrollIntoView());
    return true;
  };
}
/**
 * Toggle between wrapping an inactive node with the provided node type, and
 * lifting it up into it's parent.
 *
 * @param nodeType - the node type to toggle
 * @param attrs - the attrs to use for the node
 */

function toggleWrap(nodeType) {
  var attrs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return parameter => {
    var tr = parameter.tr,
        state = parameter.state;
    var type = isString(nodeType) ? state.schema.nodes[nodeType] : nodeType;
    var isActive = isNodeActive({
      state: tr,
      type
    });

    if (isActive) {
      return lift(parameter);
    }

    return wrapIn(nodeType, attrs)(parameter);
  };
}
/**
 * Returns a command that tries to set the selected textblocks to the
 * given node type with the given attributes.
 *
 * @param nodeType - the name of the node or the [[`NodeType`]].
 */

function setBlockType(nodeType, attrs, range) {
  return function (parameter) {
    var tr = parameter.tr,
        dispatch = parameter.dispatch,
        state = parameter.state;
    var type = isString(nodeType) ? state.schema.nodes[nodeType] : nodeType;

    var _ref4 = range !== null && range !== void 0 ? range : tr.selection,
        from = _ref4.from,
        to = _ref4.to;

    var applicable = false;
    tr.doc.nodesBetween(from, to, (node, pos) => {
      if (applicable) {
        return false;
      }

      if (!node.isTextblock || node.hasMarkup(type, attrs)) {
        return;
      }

      if (node.type === type) {
        applicable = true;
        return;
      }

      var $pos = tr.doc.resolve(pos);
      var index = $pos.index();
      applicable = $pos.parent.canReplaceWith(index, index + 1, type);
      return;
    });

    if (!applicable) {
      return false;
    }

    if (dispatch) {
      dispatch(tr.setBlockType(from, to, type, attrs).scrollIntoView());
    }

    return true;
  };
}

/**
 * Toggle a block between the provided type and toggleType.
 *
 * @param params - the destructured params
 */
function toggleBlockItem(toggleParameter) {
  return parameter => {
    var tr = parameter.tr;
    var type = toggleParameter.type,
        toggleType = toggleParameter.toggleType,
        attrs = toggleParameter.attrs;
    var isActive = isNodeActive({
      state: tr,
      type,
      attrs
    });

    if (isActive) {
      return setBlockType(toggleType)(parameter);
    }

    return setBlockType(type, attrs)(parameter);
  };
}

/**
 * Taken from https://stackoverflow.com/a/4900484
 *
 * Check that the browser is chrome. Supports passing a minimum version to check
 * that it is a greater than or equal to this version.
 */
function isChrome() {
  var minVersion = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var parsedAgent = navigator.userAgent.match(/Chrom(e|ium)\/(\d+)\./);
  return parsedAgent ? Number.parseInt(parsedAgent[2], 10) >= minVersion : false;
}
/**
 * Checks the selection for the current state and updates the active transaction
 * to a selection that is consistent with the initial selection.
 *
 * @param state - the editor state before any updates
 * @param tr - the transaction which has been updated and may have impacted the
 * selection.
 */

function preserveSelection(selection, tr) {
  // Get the previous movable part of the cursor selection.
  var head = selection.head; // Map this movable cursor selection through each of the steps that have happened in
  // the transaction.

  var _iterator = _createForOfIteratorHelper(tr.steps),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var step = _step.value;
      var map = step.getMap();
      head = map.map(head);
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  if (selection.empty) {
    // Update the transaction with the new text selection.
    tr.setSelection(TextSelection.create(tr.doc, head));
  } else {
    tr.setSelection(TextSelection.create(tr.doc, selection.anchor, head));
  }
}
/**
 * Replaces text with an optional appended string at the end.
 *
 * @param params - the destructured params
 */

function replaceText(parameter) {
  var range = parameter.range,
      type = parameter.type,
      _parameter$attrs2 = parameter.attrs,
      attrs = _parameter$attrs2 === void 0 ? {} : _parameter$attrs2,
      _parameter$appendText = parameter.appendText,
      appendText = _parameter$appendText === void 0 ? '' : _parameter$appendText,
      _parameter$content = parameter.content,
      content = _parameter$content === void 0 ? '' : _parameter$content,
      _parameter$keepSelect = parameter.keepSelection,
      keepSelection = _parameter$keepSelect === void 0 ? false : _parameter$keepSelect;
  return (_ref5) => {
    var state = _ref5.state,
        tr = _ref5.tr,
        dispatch = _ref5.dispatch;
    var schema = state.schema;
    var selection = tr.selection;
    var index = selection.$from.index();

    var _ref6 = range !== null && range !== void 0 ? range : selection,
        from = _ref6.from,
        to = _ref6.to;

    if (isNodeType(type)) {
      if (!selection.$from.parent.canReplaceWith(index, index, type)) {
        return false;
      }

      tr.replaceWith(from, to, type.create(attrs, content ? schema.text(content) : undefined));
    } else {
      !content ? process.env.NODE_ENV !== "production" ? invariant(false, {
        message: '`replaceText` cannot be called without content when using a mark type'
      }) : invariant(false) : void 0;
      tr.replaceWith(from, to, schema.text(content, isMarkType(type) ? [type.create(attrs)] : undefined));
    } // Only append the text if text is provided (ignore the empty string).


    if (appendText) {
      tr.insertText(appendText);
    }

    if (keepSelection) {
      preserveSelection(state.selection, tr);
    }

    if (dispatch) {
      // A workaround for a chrome bug
      // https://github.com/ProseMirror/prosemirror/issues/710#issuecomment-338047650
      if (isChrome(60)) {
        var _document$getSelectio;

        (_document$getSelectio = document.getSelection()) === null || _document$getSelectio === void 0 ? void 0 : _document$getSelectio.empty();
      }

      dispatch(tr);
    }

    return true;
  };
}

/**
 * Removes a mark from the current selection or provided range.
 *
 * @param params - the destructured params
 */
function removeMark(parameter) {
  return (_ref7) => {
    var dispatch = _ref7.dispatch,
        tr = _ref7.tr;
    var type = parameter.type,
        _parameter$expand = parameter.expand,
        expand = _parameter$expand === void 0 ? false : _parameter$expand,
        range = parameter.range;
    var selection = tr.selection;

    var _ref8 = range !== null && range !== void 0 ? range : selection,
        from = _ref8.from,
        to = _ref8.to;

    if (expand) {
      var _getMarkRange;

      var _ref9 = range ? getMarkRange(tr.doc.resolve(range.from), type) || isNumber(range.to) && getMarkRange(tr.doc.resolve(range.to), type) || {
        from,
        to
      } : isSelectionEmpty(tr) ? (_getMarkRange = getMarkRange(selection.$anchor, type)) !== null && _getMarkRange !== void 0 ? _getMarkRange : {
        from,
        to
      } : {
        from,
        to
      };

      from = _ref9.from;
      to = _ref9.to;
    }

    dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.removeMark(from, isNumber(to) ? to : from, type));
    return true;
  };
}

/**
 * Keycode for ALT key.
 */

var ALT = ['Alt', '⌥'];
/**
 * Keycode for CTRL key.
 */

var CTRL = ['Control', '⌃'];
/**
 * Keycode for COMMAND/META key.
 */

var COMMAND = ['Meta', '⌘'];
/**
 * Keycode for SHIFT key.
 */

var SHIFT = ['Shift', '⇧'];
/**
 * Keycode for the windows key
 */

var WINDOWS = ['Windows', '', '❖'];
/**
 * Keycode and symbols for caps lock
 */

var CAPS_LOCK = ['CapsLock', '⇪', '⇪'];
/**
 * Shorthand names for common modifier key combinations which should `just` work on Apple and PC.
 */

var Modifier = {
  Primary: isMac => isMac ? [COMMAND[0]] : [CTRL[0]],
  PrimaryShift: isMac => isMac ? [SHIFT[0], COMMAND[0]] : [CTRL[0], SHIFT[0]],
  PrimaryAlt: isMac => isMac ? [ALT[0], COMMAND[0]] : [CTRL[0], ALT[0]],
  Secondary: isMac => isMac ? [SHIFT[0], ALT[0], COMMAND[0]] : [CTRL[0], SHIFT[0], ALT[0]],
  Access: isMac => isMac ? [CTRL[0], ALT[0]] : [SHIFT[0], ALT[0]],
  Ctrl: () => [CTRL[0]],
  Alt: () => [ALT[0]],
  CtrlShift: () => [CTRL[0], SHIFT[0]],
  Shift: () => [SHIFT[0]],
  ShiftAlt: () => [SHIFT[0], ALT[0]]
};
/**
 * The cross platform modifier key combination names.
 */

/**
 * Returns true if this is an apple environment either on the server or the client.
 */
function isApple() {
  return environment.isApple;
}
/**
 * Create a consistent cross platform modifier string pattern.
 *
 * This can be used with the pressKeyWithModifier.
 *
 * ```ts
 * mod('Primary', 'A')
 * // => 'Control-A' // on PC
 * // => 'Cmd-A' // on Mac
 * ```
 *
 * @param modifier - A named modifier which is consistent across platforms
 *                  e.g. 'Primary' refers to 'Meta' on a mac and 'Control' on a PC
 * @param key - the key to press with the modifier. e.g. `Space` | `Enter`
 * @param [isApple] - a method which returns true when this is an apple device.
 */

function mod(modifier, key) {
  var isMacFn = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : isApple;

  switch (modifier) {
    case 'Primary':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'PrimaryShift':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'PrimaryAlt':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'Secondary':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'Access':
      return "".concat(Modifier[modifier](isMacFn()).join('-'), "-").concat(key);

    case 'Ctrl':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    case 'Alt':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    case 'CtrlShift':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    case 'Shift':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    case 'ShiftAlt':
      return "".concat(Modifier[modifier]().join('-'), "-").concat(key);

    default:
      throw new Error('Invalid modifier name passed in');
  }
}

/**
 * Iterates over descendants of a given `node`, returning child nodes predicate
 * returns truthy for.
 *
 * @remarks
 *
 * It doesn't descend into a node when descend argument is `false` (defaults to
 * `true`).
 *
 * ```ts
 * const textNodes = findChildren({
 *   node: state.doc,
 *   predicate: child => child.isText,
 *   descend: false
 * });
 * ```
 */
function findChildren(parameter) {
  var node = parameter.node,
      predicate = parameter.predicate,
      _parameter$descend = parameter.descend,
      descend = _parameter$descend === void 0 ? true : _parameter$descend,
      action = parameter.action; // Ensure that the node provided is a `ProsemirrorNode`.

  !isProsemirrorNode(node) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.INTERNAL,
    message: 'Invalid "node" parameter passed to "findChildren".'
  }) : invariant(false) : void 0; // Ensure that the predicate is a function.

  !isFunction(predicate) ? process.env.NODE_ENV !== "production" ? invariant(false, {
    code: ErrorConstant.INTERNAL,
    message: 'Invalid "predicate" parameter passed to "findChildren".'
  }) : invariant(false) : void 0; // This is used to keep track of all the node positions.

  var result = []; // Start descending into the provided node. This can be an expensive operation
  // if the document is very large or deeply nested.

  node.descendants((child, pos) => {
    var nodeWithPosition = {
      node: child,
      pos
    }; // True when this call matches the required condition - returns `true`.

    var isMatch = predicate(nodeWithPosition);

    if (!isMatch) {
      // Move onto the next node or descendant depending on the value of
      // `descend`.
      return descend;
    } // Store the result and run the provided action if it exists.


    result.push(nodeWithPosition);
    action === null || action === void 0 ? void 0 : action(nodeWithPosition);
    return descend;
  });
  return result;
}
/**
 * A utility for creating methods that find a node by a specific condition.
 */

function findNodeByPredicate(_ref) {
  var predicate = _ref.predicate;
  return parameter => findChildren(_objectSpread(_objectSpread({}, parameter), {}, {
    predicate
  }));
}
/**
 * Returns text nodes of a given `node`.
 *
 * @remarks
 * It doesn't descend into a node when descend argument is `false` (defaults to
 * `true`).
 *
 * ```ts
 * const textNodes = findTextNodes({ node });
 * ```
 */


var findTextNodes = /*#__PURE__*/findNodeByPredicate({
  predicate: child => child.node.isText
});
/**
 * Returns inline nodes of a given `node`.
 *
 * @remarks
 * It doesn't descend into a node when descend argument is `false` (defaults to
 * `true`).
 *
 * ```ts
 * const inlineNodes = findInlineNodes(node);
 * ```
 */

var findInlineNodes = /*#__PURE__*/findNodeByPredicate({
  predicate: child => child.node.isInline
});
/**
 * Returns block descendants of a given `node`.
 *
 * @remarks
 *
 * It doesn't descend into a node when descend argument is `false` (defaults to
 * `true`).
 *
 * ```ts
 * const blockNodes = findBlockNodes(node);
 * ```
 */

var findBlockNodes = /*#__PURE__*/findNodeByPredicate({
  predicate: child => child.node.isBlock
});

/**
 * Iterates over descendants of a given `node`, returning child nodes predicate
 * returns truthy for.
 *
 * @remarks
 *
 * It doesn't descend into a node when descend argument is `false` (defaults to
 * `true`).
 *
 * The following will match any node with an `id` of any value (as long as the
 * attribute exists) and a `colspan` of `2`.
 *
 * ```ts
 * const mergedCells = findChildrenByAttribute({
 *   node: table,
 *   attrs: { colspan: 2, id: (_, exists) => exists }
 * });
 * ```
 */
function findChildrenByAttribute(parameter) {
  var attrs = parameter.attrs,
      rest = _objectWithoutProperties(parameter, ["attrs"]);
  /**
   * The predicate function which loops through the provided attributes check if
   * they are valid.
   */


  function predicate(nodeWithPos) {
    var attributeKeys = new Set(keys(nodeWithPos.node.attrs));

    var _iterator = _createForOfIteratorHelper(entries(attrs)),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _step$value = _slicedToArray(_step.value, 2),
            attr = _step$value[0],
            expectedValue = _step$value[1];

        var value = nodeWithPos.node.attrs[attr];

        if ( // The user has passed in a predicate checking function.
        isFunction(expectedValue)) {
          var exists = attributeKeys.has(attr);

          if ( // Check if the predicate checker returns false, in which case we can
          // exit early.
          !expectedValue({
            value,
            exists
          })) {
            return false;
          }

          continue;
        }

        if ( // If the value doesn't match the expected value, exit early.
        value !== expectedValue) {
          return false;
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    return true;
  }

  return findChildren(_objectSpread(_objectSpread({}, rest), {}, {
    predicate
  }));
}

/**
 * Iterates over descendants of a given `node`, returning child nodes of a given
 * nodeType.
 *
 * @remarks
 *
 * It doesn't descend into a node when descend argument is `false` (defaults to
 * `true`).
 *
 * ```ts
 * const cells = findChildrenByNode({ node: state.doc, type: state.schema.nodes.tableCell });
 * ```
 */
function findChildrenByNode(parameter) {
  var type = parameter.type,
      rest = _objectWithoutProperties(parameter, ["type"]);

  return findChildren(_objectSpread(_objectSpread({}, rest), {}, {
    predicate: child => child.node.type === type
  }));
}

/**
 * Iterates over descendants of a given `node`, returning child nodes that have
 * a mark of a given markType.
 *
 * @remarks
 *
 * It doesn't descend into a `node` when descend argument is `false` (defaults
 * to `true`).
 *
 * ```ts
 * const nodes = findChildrenByMark({ node: state.doc, mark: schema.marks.strong });
 * ```
 */
function findChildrenByMark(paramter) {
  var type = paramter.type,
      rest = _objectWithoutProperties(paramter, ["type"]);

  return findChildren(_objectSpread(_objectSpread({}, rest), {}, {
    predicate: child => bool(type.isInSet(child.node.marks))
  }));
}

/**
 * Returns `true` if a given node contains nodes of a given `nodeType`.
 *
 * @remarks
 *
 * ```ts
 * if (containsNodesOfType({ node: state.doc, type: schema.nodes.listItem })) {
 *   log('contained')
 * }
 * ```
 */
function containsNodesOfType(parameter) {
  var node = parameter.node,
      type = parameter.type;
  return findChildrenByNode({
    node,
    type
  }).length > 0;
}

/**
 * Get all the changed nodes from the provided transaction.
 *
 * The following example will give us all the text nodes in the provided
 * transaction.
 *
 * ```ts
 * import { getChangedNodes } from 'remirror/core';
 *
 * const changedTextNodes = getChangeNodes(tr, { descend: true, predicate: (node) => node.isText });
 * ```
 */
function getChangedNodes(tr) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var _options$descend = options.descend,
      descend = _options$descend === void 0 ? false : _options$descend,
      predicate = options.predicate,
      StepTypes = options.StepTypes;
  var nodeRange = getChangedNodeRanges(tr, StepTypes); // The container for the nodes which have been added..

  var nodes = [];

  var _iterator2 = _createForOfIteratorHelper(nodeRange),
      _step2;

  try {
    var _loop = function _loop() {
      var range = _step2.value;
      var start = range.start,
          end = range.end; // Find all the nodes between the provided node range.

      tr.doc.nodesBetween(start, end, (node, pos) => {
        var _predicate;

        // Check wether this is a node that should be added.
        var shouldAdd = (_predicate = predicate === null || predicate === void 0 ? void 0 : predicate(node, pos, range)) !== null && _predicate !== void 0 ? _predicate : true;

        if (shouldAdd) {
          nodes.push({
            node,
            pos
          });
        }

        return descend;
      });
    };

    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      _loop();
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }

  return nodes;
}

/**
 * Creates a paste rule based on the provided regex for the provided mark type.
 *
 * TODO extract this into a separate package
 * - All contained in one plugin.
 * - Support for node paste rules
 * - Support for pasting different kinds of content.
 */
function markPasteRule(parameter) {
  var regexp = parameter.regexp,
      type = parameter.type,
      getAttributes = parameter.getAttributes;

  var handler = fragment => {
    var nodes = [];
    fragment.forEach(child => {
      if (child.isText) {
        var _child$text;

        var text = (_child$text = child.text) !== null && _child$text !== void 0 ? _child$text : '';
        var pos = 0;
        findMatches(text, regexp).forEach(match => {
          if (!match[1]) {
            return;
          }

          var start = match.index;
          var end = start + match[0].length;
          var attributes = isFunction(getAttributes) ? getAttributes(match) : getAttributes;

          if (start > 0) {
            nodes.push(child.cut(pos, start));
          }

          nodes.push(child.cut(start, end).mark(type.create(attributes).addToSet(child.marks)));
          pos = end;
        });

        if (text && pos < text.length) {
          nodes.push(child.cut(pos));
        }
      } else {
        nodes.push(child.copy(handler(child.content)));
      }
    });
    return Fragment.fromArray(nodes);
  };

  return new Plugin({
    key: new PluginKey('pasteRule'),
    props: {
      transformPasted: slice => new Slice(handler(slice.content), slice.openStart, slice.openEnd)
    }
  });
}
/**
 * Creates an input rule based on the provided regex for the provided mark type.
 */

function markInputRule(parameter) {
  var regexp = parameter.regexp,
      type = parameter.type,
      getAttributes = parameter.getAttributes,
      _parameter$ignoreWhit = parameter.ignoreWhitespace,
      ignoreWhitespace = _parameter$ignoreWhit === void 0 ? false : _parameter$ignoreWhit,
      beforeDispatch = parameter.beforeDispatch,
      updateCaptured = parameter.updateCaptured;
  var rule = new InputRule(regexp, (state, match, start, end) => {
    var _updateCaptured, _details$captureGroup, _details$fullMatch, _details$start, _details$end, _rule$shouldSkip;

    var tr = state.tr; // These are the attributes which are added to the mark and they can be
    // obtained from the match if a function is provided.

    var attributes = isFunction(getAttributes) ? getAttributes(match) : getAttributes;
    var $from = state.doc.resolve(start);
    var $to = state.doc.resolve(end);

    if (rule.invalidMarks && markActiveInRange({
      $from,
      $to
    }, rule.invalidMarks)) {
      return null;
    } // Update the internal values with the user provided method.


    var details = (_updateCaptured = updateCaptured === null || updateCaptured === void 0 ? void 0 : updateCaptured({
      captureGroup: match[1],
      fullMatch: match[0],
      start,
      end
    })) !== null && _updateCaptured !== void 0 ? _updateCaptured : {}; // Store the updated values or the original.

    var captureGroup = (_details$captureGroup = details.captureGroup) !== null && _details$captureGroup !== void 0 ? _details$captureGroup : match[1];
    var fullMatch = (_details$fullMatch = details.fullMatch) !== null && _details$fullMatch !== void 0 ? _details$fullMatch : match[0];
    start = (_details$start = details.start) !== null && _details$start !== void 0 ? _details$start : start;
    end = (_details$end = details.end) !== null && _details$end !== void 0 ? _details$end : end;
    var markEnd = end;
    var initialStoredMarks = []; // This helps prevent matches which are only whitespace from triggering an
    // update.

    if (ignoreWhitespace && (captureGroup === null || captureGroup === void 0 ? void 0 : captureGroup.trim()) === '') {
      return null;
    }

    if ((_rule$shouldSkip = rule.shouldSkip) === null || _rule$shouldSkip === void 0 ? void 0 : _rule$shouldSkip.call(rule, {
      state,
      captureGroup,
      fullMatch,
      start,
      end,
      ruleType: 'mark'
    })) {
      return null;
    }

    if (captureGroup) {
      var _tr$storedMarks;

      var startSpaces = fullMatch.search(/\S/);
      var textStart = start + fullMatch.indexOf(captureGroup);
      var textEnd = textStart + captureGroup.length;
      initialStoredMarks = (_tr$storedMarks = tr.storedMarks) !== null && _tr$storedMarks !== void 0 ? _tr$storedMarks : [];

      if (textEnd < end) {
        tr.delete(textEnd, end);
      }

      if (textStart > start) {
        tr.delete(start + startSpaces, textStart);
      }

      markEnd = start + startSpaces + captureGroup.length;
    }

    tr.addMark(start, markEnd, type.create(attributes)); // Make sure not to reactivate any marks which had previously been
    // deactivated. By keeping track of the initial stored marks we are able to
    // discard any unintended consequences of deleting text and adding it again.

    tr.setStoredMarks(initialStoredMarks); // Allow the caller of this method to update the transaction before it is
    // returned and dispatched by ProseMirror.

    beforeDispatch === null || beforeDispatch === void 0 ? void 0 : beforeDispatch({
      tr,
      match,
      start,
      end
    });
    return tr;
  });
  return rule;
}
/**
 * Creates a node input rule based on the provided regex for the provided node
 * type.
 *
 * Input rules transform content as the user types based on whether a match is
 * found with a sequence of characters.
 */

function nodeInputRule(parameter) {
  var regexp = parameter.regexp,
      type = parameter.type,
      getAttributes = parameter.getAttributes,
      beforeDispatch = parameter.beforeDispatch;
  var rule = new InputRule(regexp, (state, match, start, end) => {
    var _rule$shouldSkip2;

    var attributes = isFunction(getAttributes) ? getAttributes(match) : getAttributes;
    var tr = state.tr;
    var captureGroup = match[1];
    var fullMatch = match[0];

    if ((_rule$shouldSkip2 = rule.shouldSkip) === null || _rule$shouldSkip2 === void 0 ? void 0 : _rule$shouldSkip2.call(rule, {
      state,
      captureGroup,
      fullMatch,
      start,
      end,
      ruleType: 'plain'
    })) {
      return null;
    }

    tr.replaceWith(start - 1, end, type.create(attributes));
    beforeDispatch === null || beforeDispatch === void 0 ? void 0 : beforeDispatch({
      tr,
      match,
      start,
      end
    });
    return tr;
  });
  return rule;
}
/**
 * Creates a plain rule based on the provided regex. You can see this being used
 * in the `@remirror/extension-emoji` when it is setup to use plain text.
 */

function plainInputRule(parameter) {
  var regexp = parameter.regexp,
      transformMatch = parameter.transformMatch,
      beforeDispatch = parameter.beforeDispatch;
  var rule = new InputRule(regexp, (state, match, start, end) => {
    var _rule$shouldSkip3;

    var value = transformMatch(match);

    if (isNullOrUndefined(value)) {
      return null;
    }

    var tr = state.tr;
    var captureGroup = match[1];
    var fullMatch = match[0];

    if ((_rule$shouldSkip3 = rule.shouldSkip) === null || _rule$shouldSkip3 === void 0 ? void 0 : _rule$shouldSkip3.call(rule, {
      state,
      captureGroup,
      fullMatch,
      start,
      end,
      ruleType: 'plain'
    })) {
      return null;
    }

    if (value === '') {
      tr.delete(start, end);
    } else {
      tr.replaceWith(start, end, state.schema.text(value));
    }

    beforeDispatch === null || beforeDispatch === void 0 ? void 0 : beforeDispatch({
      tr,
      match,
      start,
      end
    });
    return tr;
  });
  return rule;
}

export { ALT, CAPS_LOCK, COMMAND, CTRL, Modifier, SHIFT, WINDOWS, applyClonedTransaction, areSchemasCompatible, areStatesEqual, atDocEnd, atDocStart, canInsertNode, chainKeyBindingCommands, cloneTransaction, closestElement, containsNodesOfType, createDocumentNode, endPositionOfParent, environment, findBlockNodes, findChildren, findChildrenByAttribute, findChildrenByMark, findChildrenByNode, findElementAtPosition, findInlineNodes, findNodeAtPosition, findNodeAtSelection, findParentNode, findParentNodeOfType, findPositionOfNodeAfter, findPositionOfNodeBefore, findSelectedNodeOfType, findTextNodes, fromHtml, getChangedNodeRanges, getChangedNodes, getChangedRanges, getCursor, getDocument, getInvalidContent, getMarkAttributes, getMarkRange, getMatchString, getNearestNonTextElement, getRemirrorJSON, getSelectedGroup, getSelectedWord, getTextContentFromSlice, getTextSelection, hasTransactionChanged, isAllSelection, isApple, isChrome, isDocNode, isDocNodeEmpty, isDomNode, isEditorSchema, isEditorState, isElementDomNode, isEmptyBlockNode, isMarkActive, isMarkType, isNodeActive, isNodeOfType, isNodeSelection, isNodeType, isProsemirrorMark, isProsemirrorNode, isRemirrorJSON, isResolvedPos, isSelection, isSelectionEmpty, isTextDomNode, isTextSelection, isTransaction, lift, markEqualsType, markInputRule, markPasteRule, mergeKeyBindings, mergeProsemirrorKeyBindings, mod, nodeInputRule, omitExtraAttributes, plainInputRule, preserveSelection, removeMark, removeNodeAfter, removeNodeAtPosition, removeNodeBefore, replaceNodeAtPosition, replaceText, schemaToJSON, setBlockType, shouldUseDomEnvironment, startPositionOfParent, toDom, toHtml, toggleBlockItem, toggleWrap, updateMark, wrapIn };
